import React from 'react';
import { motion } from 'framer-motion';
import { Scissors, Factory, Shirt, Sparkles, Droplets, Cpu, Shield, Eye } from 'lucide-react';

const capabilities = [
  {
    icon: Scissors,
    title: "Cutting Room",
    description: "Lectra CAD machines for optimized cutting efficiency. Band knife and straight knife machines with component labeling for accuracy.",
    stats: "Precision Cutting",
  },
  {
    icon: Eye,
    title: "Fabric Checking",
    description: "Rigorous 4-Point Quality System for incoming fabric inspection ensuring only premium materials enter production.",
    stats: "4-Point System",
  },
  {
    icon: Factory,
    title: "Sewing",
    description: "800+ machines across 21 production lines. Separate lines for blouses, dresses, denim bottoms, trousers, and more.",
    stats: "200K+ Pieces/Month",
  },
  {
    icon: Shirt,
    title: "Finishing & Packing",
    description: "Complete finishing with buttoning, ironing, and quality checks. Pneumatic snap machines and rigorous inspection protocols.",
    stats: "AQL 2.5 Standard",
  },
  {
    icon: Droplets,
    title: "In-House Laundry",
    description: "Equipped for normal washes, denim washing, and garment dyeing. Front-loading and belly washing machines.",
    stats: "5000 Pieces/Day",
  },
  {
    icon: Cpu,
    title: "Computer Embroidery",
    description: "In-house Tajima embroidery machines with sequin attachments for detailed embellishments and custom designs.",
    stats: "Tajima Technology",
  },
  {
    icon: Shield,
    title: "Product Safety",
    description: "Following international protocols like REACH to ensure every garment is safe. Complete chemical safety and manufacturing safety compliance.",
    stats: "100% Needle Detection",
  },
];

const CapabilitiesSection = () => {
  return (
    <section 
      id="capabilities"
      data-testid="capabilities-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16"
        >
          <p className="font-sans text-sm font-medium tracking-[0.3em] uppercase text-slate-400 mb-4">
            Our Capabilities
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-slate-900">
              End-to-End<br />
              <span className="text-rose-700">Manufacturing Excellence</span>
            </h2>
            <p className="font-sans text-lg text-slate-600 max-w-md">
              75,000+ sq.ft of modern facilities equipped with the latest textile machinery 
              and a well-defined work process.
            </p>
          </div>
        </motion.div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Left - Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-2 gap-4"
          >
            <div className="col-span-2 aspect-[16/9] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/k2g4oppk_DSC_0561.jpeg"
                alt="Computer Embroidery"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="aspect-square overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/ikt9z2lt_SPotiC_71-_XKc_GFtHB-.avif"
                alt="In-House Laundry Facility"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="aspect-square overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/dylb8tyd_DSC_0479.jpeg"
                alt="Sewing Production Floor"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="aspect-square overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/4h2wav6w_DZocpUQ4VWoWrEDuOLV0M.avif"
                alt="Safety & Needle Detection"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="aspect-square overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/8ydphq2v_sy2ld7TgNkfXQ7P5w3kth.avif"
                alt="Sampling & Merchandising"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </motion.div>

          {/* Right - Capabilities List */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="space-y-6"
          >
            {capabilities.map((cap, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group flex gap-6 p-6 bg-slate-50 hover:bg-white border border-transparent hover:border-slate-200 transition-all duration-500 hover:shadow-lg"
                data-testid={`capability-${index}`}
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-rose-700/10 flex items-center justify-center group-hover:bg-rose-700 transition-colors duration-300">
                    <cap.icon 
                      size={24} 
                      className="text-rose-700 group-hover:text-white transition-colors duration-300" 
                      strokeWidth={1.5} 
                    />
                  </div>
                </div>
                <div className="flex-grow">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-serif text-lg text-slate-900">
                      {cap.title}
                    </h3>
                    <span className="font-sans text-xs text-rose-700 bg-rose-50 px-2 py-1 uppercase tracking-wider">
                      {cap.stats}
                    </span>
                  </div>
                  <p className="font-sans text-sm text-slate-600 leading-relaxed">
                    {cap.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Bottom Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 p-8 md:p-12 bg-slate-900"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-serif text-3xl md:text-4xl font-medium text-white">200K+</p>
              <p className="font-sans text-xs text-white/60 uppercase tracking-wider mt-2">Wovens/Month</p>
            </div>
            <div>
              <p className="font-serif text-3xl md:text-4xl font-medium text-white">150K+</p>
              <p className="font-sans text-xs text-white/60 uppercase tracking-wider mt-2">Knits/Month</p>
            </div>
            <div>
              <p className="font-serif text-3xl md:text-4xl font-medium text-white">800+</p>
              <p className="font-sans text-xs text-white/60 uppercase tracking-wider mt-2">Machines</p>
            </div>
            <div>
              <p className="font-serif text-3xl md:text-4xl font-medium text-white">21</p>
              <p className="font-sans text-xs text-white/60 uppercase tracking-wider mt-2">Production Lines</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CapabilitiesSection;
